import React, { useEffect, useState } from "react";
import { Trash2, Plus, Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { bizList, bizAdd, bizDel, bizBriefNow } from "../lib/api";

export const PASTE_PROMPT = `Please add a read-only "Nexus health link" to this app so my Nexus TV can show business health.

Build: GET /api/nexus-health
- Security: require header X-Nexus-Key matching an env var NEXUS_HEALTH_KEY (generate a long random key, show it to me). Return 401 otherwise. Read-only, no customer personal data.
- Return JSON exactly in this shape (numbers in USD):
{
  "business": "<business name>",
  "currency": "USD",
  "today": { "sales": <today's revenue>, "orders": <today's order count> },
  "cash_in_bank": <current cash balance, or null if unknown>,
  "revenue_daily": [ { "date": "YYYY-MM-DD", "revenue": <number> } ]   // last 14 days, oldest first
  "revenue_weekly": [ { "week": "YYYY-Www", "revenue": <number> } ]   // last 8 weeks, oldest first
  "health": "green" | "amber" | "red",   // your overall judgement of the business today
  "health_score": <0-100>,
  "alert": "<the single most important thing I should know today, max 1 sentence>"
}
Use real data from this app's database. When done, give me the full https URL of the endpoint and the key.`;

export const RemoteBusiness = ({ userId }) => {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", url: "", key: "" });
  const [busy, setBusy] = useState(false);
  const load = () => bizList(userId).then((d) => setList(d.businesses || [])).catch(() => {});
  useEffect(() => { load(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    setBusy(true);
    try {
      const r = await bizAdd(userId, f);
      if (r.test?.error) toast.warning(`Saved, but Nexus can't read it yet: ${r.test.error}`);
      else toast.success(`${r.test?.name || f.name} connected`);
      setF({ name: "", url: "", key: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add"); }
    setBusy(false);
  };
  const copy = () => navigator.clipboard.writeText(PASTE_PROMPT).then(() => toast.success("Copied. Paste it into each business app."));
  const inp = "w-full bg-white/5 border border-cyan-300/15 rounded-xl px-3 py-3 outline-none text-sm";

  return (
    <section className="space-y-3" data-testid="remote-business">
      <p className="text-xs tracking-[0.3em] text-cyan-300/60 font-bold">BUSINESSES ON TV</p>
      <button onClick={copy} data-testid="remote-biz-copy-prompt" className="w-full rounded-xl py-3 bg-amber-400/15 border border-amber-300/30 text-amber-200 font-bold flex items-center justify-center gap-2"><Copy className="w-4 h-4" /> Copy request for my business apps</button>
      {list.map((b) => (
        <div key={b.id} className="flex items-center justify-between bg-white/5 rounded-xl px-3 py-3 text-sm" data-testid={`remote-biz-${b.id}`}>
          <div className="min-w-0"><p className="font-bold">{b.name}</p><p className="text-cyan-300/50 truncate text-xs">{b.url}</p></div>
          <button onClick={() => bizDel(userId, b.id).then(load)} data-testid={`remote-biz-del-${b.id}`} className="text-rose-300/70 p-2"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
      <button onClick={() => bizBriefNow(userId).then(() => toast.success("Nexus is speaking the business brief on the TV")).catch((e) => toast.error(e?.response?.data?.detail || "Could not start brief"))}
        disabled={!list.length} data-testid="remote-biz-brief-now" className="w-full rounded-xl py-3 bg-emerald-400/15 border border-emerald-300/30 text-emerald-200 font-bold disabled:opacity-40">Speak business brief on TV now</button>
      <input className={inp} placeholder="Business name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="remote-biz-name" />
      <input className={inp} placeholder="https://…/api/nexus-health" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} data-testid="remote-biz-url" />
      <input className={inp} placeholder="Key" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })} data-testid="remote-biz-key" />
      <button onClick={add} disabled={busy || !f.name || !f.url} data-testid="remote-biz-add" className="w-full rounded-xl py-3 bg-[#00F0FF] text-[#030712] font-bold flex items-center justify-center gap-2 disabled:opacity-40">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Connect business
      </button>
    </section>
  );
};
